import { visitorManagementListener as api } from 'st-app/api'

/**
 * Review-wizard QUEUE for the visitor grid. Strictly one wizard modal on screen at a time: the head
 * of the queue is opened, and the next one is not opened until that modal has been observed to leave
 * the DOM.
 *
 * WHY THIS IS DOM-DRIVEN AND NOT EVENT-DRIVEN. Two earlier versions latched on Bootstrap's
 * shown.bs.modal / hidden.bs.modal and both stacked a second wizard on top of the first in practice.
 * The first version never received the events at all (bound to a frame's document.body while the
 * modal is created in the ROOT window's document). Fixing the binding did not fix the stacking, which
 * means the events do not arrive in the shape this logic needs - a spurious or intermediate
 * hidden.bs.modal during the render is enough to make "the wizard closed" fire while it is still on
 * screen, and the queue then advances over the top of it. Rather than keep guessing at event
 * semantics, this polls for the modal's actual presence, which is the thing we care about and the one
 * thing that cannot lie.
 *
 * MODULE-LEVEL because st-visitor-management-listener.tsx registers its server-message handler at
 * module scope and so cannot close over component state.
 */

/** The review wizard's view GUID - VisitorCheckInCheckOutUI's RenderView in case "VM_REVIEW". */
const WIZARD_VIEW_GUID = '1F05A0AD-0419-4BC4-B3F7-4474CB7284FC';

/** How often to look at the DOM while a wizard is outstanding. */
const WATCH_INTERVAL_MS = 400;

/**
 * How long to wait for the wizard to APPEAR before concluding the open failed. Generous: the open is
 * a server round trip. Expiring releases the slot WITHOUT recording a dismissal, so the same visitor
 * is retried rather than skipped - a failed render must never silently lose someone.
 */
const APPEAR_TIMEOUT_MS = 15000;

/** GUs still to be shown, oldest check-in first, exactly as the server ordered them. */
let queue: string[] = [];

/**
 * The GU whose wizard is open, or whose open we have requested and are still waiting to see. While
 * this is non-empty NOTHING else may be opened - it is the one-at-a-time invariant, and it is cleared
 * in exactly two places: when the modal is observed to close, and when it never appeared at all.
 */
let activeGU = '';

/** True once the active GU's modal has actually been seen on screen, so its absence means "closed". */
let sawModal = false;

let watchTimer: ReturnType<typeof setInterval> | null = null;
let waitedMs = 0;

/**
 * Number of visible modals that already existed when the open was requested. The grid itself can be
 * rendered inside a modal, so "a modal is visible" is not the same as "our wizard is visible"; this
 * baseline is what makes the fallback detection below safe in that case.
 */
let baselineModals = 0;

/** Guards against overlapping queue fetches when several broadcasts land at once. */
let fetching = false;

/**
 * GUs whose wizard was shown and then CLOSED WITHOUT the row leaving REVW, since the queue last
 * changed. Skipped when choosing the next head, so closing a wizard does not immediately reopen the
 * same one - while still not losing that visitor for the session (see notifyQueueChanged).
 */
const dismissed = new Set<string>();

function rootWindow(): any {
    try {
        return window.ST?.GetRootWindow?.() ?? window;
    } catch {
        return window;
    }
}

function rootJQuery(): any {
    const r = rootWindow();
    return r.$ ?? (window as any).$;
}

/** Visible modals in the ROOT document - where ST creates them, regardless of which frame we are in. */
function visibleModalCount(): number {
    try {
        const $ = rootJQuery();
        if (!$) {
            return 0;
        }
        return $(rootWindow().document.body).find('.modal:visible').length;
    } catch {
        return 0;
    }
}

/**
 * Whether the review wizard is on screen right now.
 *
 * Prefers POSITIVE identification: a visible modal containing the wizard view's element. Falls back
 * to "more modals are visible than when we asked" only when that element cannot be found, because the
 * exact attribute the view renders is unverified - the fallback keeps this working either way, and
 * comparing against the baseline is what stops the grid's own enclosing modal counting as ours.
 */
function wizardModalPresent(): boolean {
    try {
        const $ = rootJQuery();
        if (!$) {
            return false;
        }
        const modals = $(rootWindow().document.body).find('.modal:visible');
        if (modals.length === 0) {
            return false;
        }
        const byGuid = modals.find(
            `[data-guid="${WIZARD_VIEW_GUID}"],[data-guid="${WIZARD_VIEW_GUID.toLowerCase()}"]`);
        if (byGuid.length > 0) {
            return true;
        }
        return modals.length > baselineModals;
    } catch {
        return false;
    }
}

function stopWatching() {
    if (watchTimer) {
        clearInterval(watchTimer);
        watchTimer = null;
    }
    waitedMs = 0;
}

/**
 * Watches the outstanding wizard: first for it to appear, then for it to go away. Its disappearance
 * is what dequeues the row and releases the slot for the next head - the whole one-at-a-time rule
 * lives here.
 */
function startWatching() {
    stopWatching();
    sawModal = false;
    waitedMs = 0;
    watchTimer = setInterval(() => {
        const present = wizardModalPresent();

        if (!sawModal) {
            if (present) {
                sawModal = true;
                return;
            }
            waitedMs += WATCH_INTERVAL_MS;
            if (waitedMs >= APPEAR_TIMEOUT_MS) {
                // Never rendered. Release the slot but do NOT record a dismissal, so this visitor is
                // offered again rather than being dropped because of a failed render.
                stopWatching();
                activeGU = '';
                void openNext();
            }
            return;
        }

        if (!present) {
            // Observed open, now observed gone: this row is done being shown. Recorded as dismissed so
            // it is not immediately reopened; if it was actually RESOLVED it has left REVW and the
            // server will not return it again anyway.
            const finished = activeGU;
            stopWatching();
            activeGU = '';
            if (finished) {
                dismissed.add(finished);
                queue = queue.filter(gu => gu !== finished);
            }
            void openNext();
        }
    }, WATCH_INTERVAL_MS);
}

/**
 * Opens the head of the queue, if and only if nothing is already outstanding.
 *
 * This is the ONLY place a wizard is opened, and the activeGU check at the top is the only thing that
 * needs to hold for two modals never to coexist.
 */
/**
 * Whether the Visitor Check In / Check Out screen is actually IN FRONT OF THE USER right now.
 *
 * NOT the same as "this component is mounted". The view is declared CACHE="YES", so navigating to
 * another screen leaves this component alive and still subscribed - onCleanup never runs, the SSE
 * channel is never unsubscribed, and the module-scope message handler keeps firing. ST then creates
 * the wizard modal in the ROOT window's document, which is showing whatever screen the user actually
 * navigated to. That is how a review wizard was appearing on top of unrelated screens.
 *
 * `offsetParent === null` is the standard "not rendered" test and is what other components in this
 * app already use. It is also the same class of check as wizardModalPresent() below: ask the DOM what
 * is true rather than trust a lifecycle event, because the lifecycle events here demonstrably lie.
 */
function listenerOnScreen(): boolean {
    try {
        const el = document.querySelector('[data-st-visitor-management-listener]') as HTMLElement | null;
        if (!el) {
            // No element at all means this frame's markup is gone. Treat as off-screen: opening a
            // modal from a screen that no longer exists is the bug, not the fix.
            return false;
        }
        return el.offsetParent !== null;
    } catch {
        // A DOM access that throws tells us nothing about visibility. Say "not on screen" - the cost
        // is a delayed wizard, and the retry below picks it up; the cost of guessing the other way is
        // the exact bug being fixed.
        return false;
    }
}

async function openNext() {
    if (activeGU) {
        return;
    }
    const next = queue.find(gu => !!gu && !dismissed.has(gu));
    if (!next) {
        return;
    }

    // THE WIZARD NEVER OPENS OFF THIS SCREEN. Full stop - no queueing for later, no retry, no
    // reopening when the user comes back by some other route.
    //
    // A broadcast reaches this handler wherever the user is, because the message handler is
    // registered at module scope and the view is CACHE="YES", so this component stays alive and
    // subscribed after navigation. Without this guard the wizard was appearing on top of completely
    // unrelated screens - ST builds the modal in the ROOT window's document, which is showing
    // whatever the user actually navigated to.
    //
    // The only intended way in is the original one: the user opens Visitor Check In / Check Out, the
    // component mounts, and onMount's refreshAndOffer() reads the review queue and offers the head of
    // it. A toast and chime are the ONLY things that should reach someone on another screen.
    //
    // Nothing is dismissed here - being elsewhere is not a decision about this visitor, and the row
    // stays REVW with its [Review] button either way.
    if (!listenerOnScreen()) {
        return;
    }

    activeGU = next;
    // Captured BEFORE the request so it reflects the screen without our wizard on it.
    baselineModals = visibleModalCount();
    startWatching();
    try {
        window.ST.SendServerEvent(null, 'VM_Open_Review', { '@VM_VISITOR_GU': next });
    } catch {
        // The request never went out - release the slot immediately rather than waiting out the
        // appear timeout, and leave no dismissal so it retries.
        stopWatching();
        activeGU = '';
    }
}

/**
 * Re-reads the queue from the server and opens the head if nothing is outstanding.
 *
 * Always re-fetches rather than trusting the cached list, so rows somebody else resolved have already
 * left the server's answer and are never opened pointlessly. The server returns them oldest-first;
 * this preserves that order exactly and does no ordering of its own.
 */
export async function refreshAndOffer() {
    if (fetching) {
        return;
    }
    fetching = true;
    try {
        const fromServer = await api.GetReviewQueue();
        queue = (fromServer ?? []).filter(gu => !!gu);
    } catch {
        // Queue unavailable: no auto-open. The grid still refreshed and the [Review] button still
        // works, so this degrades to the old manual behaviour rather than to a broken screen.
        return;
    } finally {
        fetching = false;
    }
    await openNext();
}

/**
 * Declares that the queue itself changed - somebody was flagged, or somebody was resolved - which
 * makes previously dismissed visitors eligible again so the OLDEST unresolved one is offered next.
 * Called from the refresh-broadcast handler, the only signal that a real write happened; a plain
 * dismissal writes nothing and so cannot clear this.
 */
export function notifyQueueChanged() {
    dismissed.clear();
}

/**
 * Resets everything for a fresh mount. MUST be called before the component starts driving the queue.
 *
 * Module state outlives the component, so a wizard that was on screen when the grid unmounted would
 * otherwise leave activeGU set forever and auto-open would be silently dead for the rest of the page
 * session, with the manual [Review] button still working and nothing indicating why.
 */
export function resetQueueState() {
    stopWatching();
    queue = [];
    activeGU = '';
    sawModal = false;
    baselineModals = 0;
    fetching = false;
    dismissed.clear();
}

/** Stops the DOM watcher on unmount so no interval outlives the screen. */
export function stopQueue() {
    stopWatching();
}

export class VisitorManagementListenerContext {
    constructor() {

    }
}
